import {
  dateFormatterDayMonthYearLong2Digits,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  sortByDateTime,
  Spacer,
  Tag,
  StatusSeverity,
  TextCapitalized,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  selectAdvancedAnalyticsVehiclesList,
  selectAdvancedAnalyticsVehiclesListStatus,
} from "../fleetSlice";
import {
  AdvancedAnalyticsVehicles,
  AdvancedAnalyticsVehiclesDisplayed,
  MileageDeviationStatus,
} from "../fleetApi";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import styled from "styled-components";
import Car from "@qivia/ui/src/designSystem/assets/Car.png";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";
import {
  formatCurrency,
  formatDate,
  formatMileage,
  formatMonth,
  formatPercent,
  getDate,
  hasBadNumberValue,
} from "../../../../libSlice/materialUiFormats";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import "../../../../i18n/i18n";
import {
  fakeAdvancedAnalyticsVehiclesForDemo,
  isMissingContractData,
} from "../lib";
import { AdvancedAnalyticsSummary } from "./advancedAnalyticsSummary";
import { DeviationCell } from "./deviationCell";
import { WarningOutlined } from "@qivia/ui/src/designSystem/materialUi/materialUi";

interface SharedInterfaceAnalyticsVehiclesTabProps {
  sidePanelAction?: (props: Partial<AdvancedAnalyticsVehicles>) => void;
  sidePanel?: JSX.Element;
}

export const SharedInterfaceAnalyticsVehiclesTab = (
  props: SharedInterfaceAnalyticsVehiclesTabProps,
) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const advancedAnalyticsVehicleListInit = useAppSelector(
    selectAdvancedAnalyticsVehiclesList,
  );
  const advancedAnalyticsVehicleListStatus = useAppSelector(
    selectAdvancedAnalyticsVehiclesListStatus,
  );
  const [listDisplayed, setListDisplayed] = useState<
    AdvancedAnalyticsVehiclesDisplayed[] | null
  >(null);

  const pageName = "fleet.advanced_analytics";

  const apiRef = useGridApiRef();
  const key = "customers-grid";
  const aggregation = {
    model: {
      reference: "size",
      registrationNumber: "size",
      driverName: "size",
      mileage: "avg",
      mensualMileage: "avg",
      monthlyMileageKmVariation: "avg",
      mileageDeviationPercentage: "avg",
      mileageVariationEndContract: "avg",
      additionalCostEndContract: "sum",
      additionalCostPerKm: "avg",
    },
  };
  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const findKeysToTranslate = useCallback(
    (key: keyof Partial<AdvancedAnalyticsVehiclesDisplayed>, value: string) => {
      switch (key) {
        case "contractStartDate":
        case "contractEndDate":
          // Demo data already ships display-ready DD/MM/YYYY dates
          if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
            return value;
          }
          return dateFormatterDayMonthYearLong2Digits(new Date(value));
        case "mileageDeviation":
          return t(`fleet.advanced_analytics.deviation.${value}`);
        default:
          return value;
      }
    },
    [t],
  );

  const isDemoEnvironment = useCallback(() => {
    return import.meta.env["VITE_TARGET_ENV"] === "demo";
  }, []);

  const keysToTranslate = useMemo(
    () =>
      [
        "contractStartDate",
        "contractEndDate",
        "mileageDeviation",
      ] as (keyof AdvancedAnalyticsVehiclesDisplayed)[],
    [],
  );

  const sourceAdvancedAnalyticsVehicleList = useMemo(() => {
    if (!isDemoEnvironment()) {
      return advancedAnalyticsVehicleListInit;
    }
    return [
      ...advancedAnalyticsVehicleListInit,
      ...fakeAdvancedAnalyticsVehiclesForDemo,
    ];
  }, [advancedAnalyticsVehicleListInit, isDemoEnvironment]);

  const advancedAnalyticsVehicleList: AdvancedAnalyticsVehicles[] =
    useMemo(() => {
      const rowsWithId = sourceAdvancedAnalyticsVehicleList.map((av) => {
        let newDict: AdvancedAnalyticsVehicles & {
          id: string;
          statusSeverity?: StatusSeverity;
        } = {
          ...av,
          id: av.uuid,
        };

        // Map mileageDeviation to statusSeverity for CSS styling
        if (av.mileageDeviation) {
          const mileageDeviationToStatusSeverity: Record<
            MileageDeviationStatus,
            StatusSeverity
          > = {
            OVERROLLING: "error",
            UNDERROLLING: "warning",
            CORRECT: "success",
          };
          newDict.statusSeverity =
            mileageDeviationToStatusSeverity[av.mileageDeviation] || undefined;
        }

        Object.entries(av).forEach(([key, value]) => {
          if (
            keysToTranslate.includes(
              key as keyof AdvancedAnalyticsVehiclesDisplayed,
            )
          ) {
            newDict = {
              ...newDict,
              [key]: value
                ? findKeysToTranslate(
                    key as keyof AdvancedAnalyticsVehiclesDisplayed,
                    value as string,
                  )
                : "",
            };
          }
        });
        return newDict;
      });

      return sortByDateTime(rowsWithId, "desc", "createdAt");
    }, [
      sourceAdvancedAnalyticsVehicleList,
      findKeysToTranslate,
      keysToTranslate,
    ]);

  useEffect(() => {
    if (
      advancedAnalyticsVehicleListInit &&
      advancedAnalyticsVehicleListStatus === "success"
    ) {
      setListDisplayed(advancedAnalyticsVehicleList);
    }
  }, [
    advancedAnalyticsVehicleList,
    advancedAnalyticsVehicleListInit,
    advancedAnalyticsVehicleListStatus,
  ]);

  const searchBarProps = {
    values: advancedAnalyticsVehicleList,
    setFilterValues: setListDisplayed,
    keysToIgnore: [
      "uuid" as const,
      "date" as const,
      "vehicleUuid" as const,
      "lastMileageDate" as const,
      "monthlyMileage" as const,
      "vehicleType" as const,
      "status" as const,
      "model" as const,
      "averageConsumption" as const,
      "critAir" as const,
      "averageEmission" as const,
      "energyClass" as const,
      "marque" as const,
      "modele" as const,
      "energie" as const,
      "createdAt" as const,
    ],
  };

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("fleet.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  const columnsDisplayed: {
    [index in keyof AdvancedAnalyticsVehiclesDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    const missingData = (
      <StyledMissingData>
        <WarningOutlined />
        <Spacer x={0.25} />
        <TextCapitalized>
          {t("fleet.advanced_analytics.missingData")}
        </TextCapitalized>
      </StyledMissingData>
    );

    const withMissing =
      (
        formatter: (value: unknown, row: Record<string, unknown>) => ReactNode,
      ) =>
      (value: unknown, row: Record<string, unknown>): ReactNode =>
        isMissingContractData(row) ? missingData : formatter(value, row);

    return {
      reference: {
        type: "string",
        width: 250,
        valueFormatter: (value) =>
          value && (
            <StyledDivImg>
              <StyledImg src={Car} alt="Car" />
              <Spacer x={0.5} />
              <Tag
                textColor={colors["colors/text/black"]}
                backgroundColor={
                  colors["colors/surfaces/background/background_level0"]
                }
                text={value}
                borderColor={colors["colors/borders/cells/cells"]}
              />
            </StyledDivImg>
          ),
      },
      registrationNumber: {
        type: "string",
      },
      driverName: {
        type: "string",
        width: 200,
      },
      mileage: {
        type: "number",
        valueFormatter: formatMileage,
      },
      mensualMileage: {
        type: "number",
        valueFormatter: formatMileage,
      },
      mileageDeviation: {
        type: "string",
        width: 220,
        valueFormatter: withMissing((value, row) => {
          if (!value) return null;
          return (
            <DeviationCell
              statusSeverity={row["statusSeverity"] as string | undefined}
              value={value as string}
            />
          );
        }),
      },
      monthlyMileageKmVariation: {
        type: "number",
        width: 220,
        valueFormatter: withMissing(
          (value, row) =>
            `${formatMileage(value as number)} ${row["mensualContractMileage"] ? "/ " + formatMileage(row["mensualContractMileage"] as number) : ""}`,
        ),
      },
      mileageDeviationPercentage: {
        type: "number",
        width: 220,
        valueFormatter: withMissing((value) => formatPercent(value as number)),
      },
      projectionEndOfContract: {
        type: "number",
        width: 250,
        valueFormatter: withMissing(
          (value, row) =>
            `${formatMileage(value as number)} ${row["contractMileage"] ? "/ " + formatMileage(row["contractMileage"] as number) : ""}`,
        ),
      },
      mileageVariationEndContract: {
        type: "number",
        width: 280,
        valueFormatter: withMissing((value) => formatMileage(value as number)),
      },
      additionalCostEndContract: {
        type: "number",
        width: 220,
        valueFormatter: withMissing((value) => formatCurrency(value as number)),
      },
      additionalCostPerKm: {
        type: "number",
        width: 180,
        valueFormatter: formatCurrency,
      },
      consumedContractDuration: {
        type: "number",
        width: 250,
        valueFormatter: withMissing(
          (value, row) =>
            `${hasBadNumberValue(value as number) ? "" : (value as number)} ${row["contractDurationMonth"] ? "/ " + formatMonth(row["contractDurationMonth"] as number) : ""}`,
        ),
      },
      remainingContractDuration: {
        type: "number",
        width: 220,
        valueFormatter: withMissing((value) => formatMonth(value as number)),
      },
      contractStartDate: {
        type: "date",
        width: 220,
        valueGetter: getDate,
        valueFormatter: withMissing((value) => formatDate(value as Date)),
      },
      contractEndDate: {
        type: "date",
        width: 220,
        valueGetter: getDate,
        valueFormatter: withMissing((value) => formatDate(value as Date)),
      },
      vehicleAnalyticCode: {
        type: "string",
        width: 200,
      },
    };
  }, [t]);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  return (
    <StyledContainer>
      <AdvancedAnalyticsSummary
        vehiclesList={sourceAdvancedAnalyticsVehicleList}
      />
      <Spacer y={1} />
      <StyledTableWrapper>
        <MaterialUiTable<
          keyof AdvancedAnalyticsVehiclesDisplayed,
          AdvancedAnalyticsVehiclesDisplayed
        >
          rows={listDisplayed}
          columns={columnsDisplayed}
          pageName={pageName}
          onRowClickAction={props.sidePanelAction}
          dataGridState={dataGridState}
          searchBar={searchBarProps}
          apiRef={apiRef}
          downloadViewAction={downloadViewAction}
        />
      </StyledTableWrapper>
      {props.sidePanel ? props.sidePanel : <></>}
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const StyledTableWrapper = styled.div`
  min-height: 0;
`;

const StyledMissingData = styled.div`
  display: flex;
  align-items: center;
  color: ${colors["colors/text/lightGrey"]};
  & svg {
    width: 1rem;
    height: 1rem;
    fill: ${colors["colors/text/lightGrey"]};
  }
`;

const StyledDivImg = styled.div`
  display: flex;
  align-items: center;
`;

const StyledImg = styled.img`
  filter: grayscale(100%);
  width: 2rem;
  height: 60px;
  object-fit: contain;
  padding-bottom: 2.5px;
`;
